import type { RailLayout } from "@/data/rail-branches";
import type {
  CompiledChapter,
  JourneyMode,
  TimeMark,
} from "@/data/schema/compiled";
import { buildCharacterColorMap } from "../../character/colors";
import { activeBeatIndex } from "@/lib/timeline/playback";
import type { MarkerAppearance } from "../../marker/MarkerPortrait/portrait";
import { withFittedLanePitch } from "../../rail/RailLanes/spacing";
import { buildCharacterMarkers } from "./layoutMarkers";
import { acquisitionsOf, steppedOutOf, visitsOf } from "./railMoments";
import { namesFromStart, revealedNamesByCharacter, type MarkerNames } from "./markerNames";
import { chapterTitleMap, eventToBeat, type SceneBeat } from "./layoutBeat";
import { buildLocationPlacementPlan } from "../../location/LocationPlacement/plan";
import type { LocationPlacementPlan } from "../../location/LocationPlacement/plan";

export const MS_PER_PIXEL = 12;
export type { SceneBeat } from "./layoutBeat";

export interface CharacterMarker extends MarkerNames {
  characterId: string;
  displayName: string;
  imageUrl?: string;
  appearances: MarkerAppearance[];
}

export interface BookTimelineLayout {
  /** Set whenever the layout comes from real book data. */
  bookId?: string;
  title: string;
  beats: SceneBeat[];
  markers: CharacterMarker[];
  totalDurationMs: number;
  characterColors: ReadonlyMap<string, string>;
  rail: RailLayout;
  /** Cast vs rail time segments for every location run (chapter load). */
  locationPlacement?: LocationPlacementPlan;
  journey?: JourneyMode;
  timeMarks?: readonly TimeMark[];
}

export function withLocationPlacement(
  base: Omit<BookTimelineLayout, "locationPlacement">,
): BookTimelineLayout {
  return {
    ...base,
    locationPlacement: buildLocationPlacementPlan(base),
  };
}

export function buildBookTimelineLayout(
  chapter: CompiledChapter,
  journey: JourneyMode = "book",
): BookTimelineLayout {
  const events = chapter.eventsByJourney[journey];
  const nameById = new Map(
    chapter.characters.map((c) => [c.id, c.displayName] as const),
  );
  const imageById = new Map(
    chapter.characters.map((c) => [c.id, {
      imageUrl: c.imageUrl,
      appearances: c.appearances.map((appearance) => ({
        id: appearance.id,
        ...(appearance.imageUrl === undefined
          ? {}
          : { imageUrl: appearance.imageUrl }),
      })),
    }] as const),
  );
  const sceneById = new Map(chapter.scenes.map((s) => [s.id, s] as const));
  const titles = chapterTitleMap(chapter);
  const beats = events.map((event) => eventToBeat(event, sceneById, titles, chapter, journey));
  const revealed = revealedNamesByCharacter(chapter);
  const startNames = new Map(chapter.characters.map((c) => [c.id, namesFromStart(c)] as const));
  const markers = buildCharacterMarkers(beats, nameById, imageById).map((marker) => {
    const start = startNames.get(marker.characterId);
    const later = start?.laterNames;
    const names = revealed.get(marker.characterId);
    return {
      ...marker,
      ...(start === undefined ? {} : { displayName: start.displayName }),
      ...(later === undefined ? {} : { laterNames: later }),
      ...(names === undefined ? {} : { revealedNames: names }),
    };
  });
  const last = beats[beats.length - 1];
  const totalDurationMs = last ? last.endMs : 0;

  const characterColors = buildCharacterColorMap(chapter.characters);
  const vehicleIds = new Set(chapter.characters
    .filter((character) => character.kind === "vehicle")
    .map((character) => character.id));
  const journeyRail = chapter.railsByJourney[journey];
  const base = {
    bookId: chapter.bookId,
    title: chapter.title,
    beats,
    markers,
    totalDurationMs,
    characterColors,
    rail: {
      ...withFittedLanePitch(vehicleIds.size === 0 ? journeyRail : {
        ...journeyRail,
        stays: journeyRail.stays.map((stay) => {
          if (vehicleIds.has(stay.characterId)) {
            return { ...stay, vehicle: true };
          }
          const vehicleId = steppedOutOf(beats, stay);
          return vehicleId === undefined ? stay : { ...stay, emergesFrom: vehicleId };
        }),
      }),
      ...acquisitionsOf(beats),
      ...visitsOf(beats),
      minorCharacterIds: chapter.characters
        .filter((character) => character.minor === true || character.kind === "vehicle")
        .map((character) => character.id),
    },
    journey,
    timeMarks: chapter.timeMarks ?? [],
  };
  return {
    ...base,
    locationPlacement: buildLocationPlacementPlan(base),
  };
}

export { presentCharacterIds } from "./layoutBeat";

/** Scenes in this beat's chapter. Universe order has no chapters, so it counts them all. */
export function sceneTotal(layout: BookTimelineLayout, beat: SceneBeat): number {
  return layout.journey === "universe"
    ? layout.beats.length
    : layout.beats.filter((item) => item.chapterId === beat.chapterId).length;
}

export function activeBeat(
  layout: BookTimelineLayout,
  playheadMs: number,
): SceneBeat | undefined {
  const index = activeBeatIndex(layout.beats, playheadMs);
  return index >= 0 ? layout.beats[index] : layout.beats[0];
}

export function cameraScrollPx(focusMs: number, viewportWidth: number): number {
  const focusPx = focusMs / MS_PER_PIXEL;
  return Math.max(0, focusPx - viewportWidth * 0.5);
}

export function worldToScreenX(worldMs: number, scrollPx: number): number {
  return worldMs / MS_PER_PIXEL - scrollPx;
}
